import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import { isLexUser } from "@/utils/lexDomain";
import OnboardingWizard from "@/components/OnboardingWizard";
import { UserRound, ChevronRight } from "lucide-react";

// Sep 2026 — Soft-Onboarding: dauerhafter Hinweis, solange das Profil
// unvollständig ist (Firmenname/Adresse, DE: Bundesland, CH: Kanton).
// Klick öffnet den Onboarding-Wizard ab Schritt 2.
export default function ProfileCompletionBanner() {
  const { user, loading, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const shouldCheck = !loading && !!user && user.onboarding_completed
    && user.tax_residency_confirmed && !isLexUser(user);

  const fetchStatus = useCallback(() => {
    api.get("/settings/profile-status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    if (shouldCheck) fetchStatus();
  }, [shouldCheck, fetchStatus]);

  if (!shouldCheck || !status || status.complete) return null;

  return (
    <>
      {!wizardOpen && (
        <div
          className="fixed bottom-4 left-4 z-[60] max-w-sm w-[calc(100vw-2rem)] sm:w-auto animate-in slide-in-from-bottom-5 duration-300"
          data-testid="profile-completion-banner"
        >
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="w-full flex items-center gap-3 bg-white border border-amber-300 rounded-xl shadow-lg p-3.5 text-left hover:bg-amber-50 transition-colors"
            data-testid="profile-completion-open-btn"
          >
            <div className="flex-shrink-0 w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
              <UserRound className="h-5 w-5 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-900">Profil vervollständigen</p>
              <p className="text-xs text-slate-500 truncate" data-testid="profile-completion-missing">
                Es fehlen: {(status.missing_labels || []).join(", ")}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-amber-500 flex-shrink-0" />
          </button>
        </div>
      )}
      {wizardOpen && (
        <OnboardingWizard
          startAtStep={1}
          completeProfileMode
          onClose={() => setWizardOpen(false)}
          onComplete={async () => {
            setWizardOpen(false);
            fetchStatus();
            refreshUser();
          }}
        />
      )}
    </>
  );
}
